import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import { categories, comics, hotComics, latestComics } from '@/mocks/comics';

function formatViews(n: number): string {
  if (n >= 10000) return (n / 10000).toFixed(1) + '万';
  return n.toString();
}

export default function Home() {
  const webPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    'name': '韩国漫画网 - 最全的韩漫网站',
    'description': '韩国漫画网是最全的韩漫网站，为您提供免费漫画在线观看和韩漫大全服务。',
    'url': 'https://www.koreamanhua.com',
    'inLanguage': 'zh-Hans',
    'isPartOf': {
      '@type': 'WebSite',
      'name': '韩国漫画网',
      'url': 'https://www.koreamanhua.com',
    },
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(webPageSchema)}</script>
      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main>
          <section className="relative w-full h-[500px] md:h-[620px] flex items-center justify-center overflow-hidden">
            <img
              src="https://readdy.ai/api/search-image?query=Korean%20manhwa%20style%20beautiful%20illustration%20with%20vibrant%20colors%2C%20artistic%20comic%20panels%20background%2C%20warm%20coral%20and%20golden%20tones%2C%20abstract%20geometric%20shapes%2C%20modern%20webtoon%20aesthetic%2C%20clean%20composition%2C%20soft%20gradient%20atmosphere%2C%20digital%20art%20wallpaper&width=1920&height=800&seq=hero-bg-home-2026&orientation=landscape"
              alt="韩国漫画网 - 最全的韩漫网站"
              className="absolute inset-0 w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/45 via-black/30 to-black/55"></div>
            <div className="relative z-10 w-full text-center px-4 md:px-6">
              <h1 className="text-3xl md:text-5xl lg:text-6xl font-heading font-bold text-white mb-4 leading-tight">
                最全的<strong className="text-accent-400 font-bold">韩国漫画</strong>平台
              </h1>
              <p className="text-base md:text-lg text-white/80 max-w-2xl mx-auto mb-8 leading-relaxed">
                海量韩漫资源，每日同步更新，免费在线观看。覆盖恋爱、奇幻、动作、搞笑、悬疑等全类型韩国漫画，韩漫大全尽在韩国漫画网。
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <Link
                  to="/category/all"
                  className="w-full sm:w-auto px-8 py-3 bg-primary-500 hover:bg-primary-600 text-background-50 font-medium rounded-lg transition-all whitespace-nowrap text-sm"
                >
                  立即浏览全部
                </Link>
                <Link
                  to="/about"
                  className="w-full sm:w-auto px-8 py-3 bg-white/10 hover:bg-white/20 text-white border border-white/30 rounded-lg transition-all whitespace-nowrap text-sm"
                >
                  了解更多
                </Link>
              </div>
            </div>
          </section>

          <div className="max-w-screen-2xl mx-auto px-4 md:px-6">
            <Breadcrumb />

            <section className="py-8 md:py-12">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950">
                  热门分类
                </h2>
                <Link to="/category/all" className="text-sm text-primary-500 hover:text-primary-600 font-medium whitespace-nowrap">
                  查看全部 <i className="ri-arrow-right-line ml-1"></i>
                </Link>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-3">
                {categories.filter(c => c.slug !== 'all').map((cat) => (
                  <Link
                    key={cat.slug}
                    to={`/category/${cat.slug}`}
                    className="group flex flex-col items-center gap-2 p-4 md:p-5 bg-background-100 hover:bg-primary-50 rounded-lg transition-all cursor-pointer"
                  >
                    <div className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-full bg-primary-100 text-primary-500 group-hover:bg-primary-500 group-hover:text-background-50 transition-all">
                      <i className={`${cat.icon} text-lg md:text-xl`}></i>
                    </div>
                    <span className="text-sm font-medium text-foreground-800 group-hover:text-primary-600 whitespace-nowrap">{cat.name}</span>
                  </Link>
                ))}
              </div>
            </section>

            <section className="py-8 md:py-12">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950">
                  热门推荐
                </h2>
                <Link to="/category/all" className="text-sm text-primary-500 hover:text-primary-600 font-medium whitespace-nowrap">
                  更多推荐 <i className="ri-arrow-right-line ml-1"></i>
                </Link>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-5">
                {hotComics.map((comic) => (
                  <Link
                    key={comic.id}
                    to={`/article/${comic.id}`}
                    className="group bg-background-100 rounded-lg overflow-hidden transition-all hover:bg-background-200/70 cursor-pointer"
                  >
                    <div className="relative w-full aspect-[3/4] overflow-hidden">
                      <img
                        src={comic.cover}
                        alt={`${comic.title} - 韩国漫画`}
                        title={`${comic.title} - 韩国漫画免费在线观看`}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                      <div className="absolute top-2 left-2 bg-primary-500 text-background-50 text-xs px-2 py-0.5 rounded-md font-medium">
                        {comic.rating}
                      </div>
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-3">
                        <span className="text-white text-xs">{comic.chapters}话</span>
                      </div>
                    </div>
                    <div className="p-3">
                      <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors truncate">
                        {comic.title}
                      </h3>
                      <p className="text-xs text-foreground-500 mt-1 truncate">{comic.author}</p>
                      <div className="flex items-center gap-1 mt-1.5 flex-wrap">
                        {comic.tags.slice(0, 2).map((tag) => (
                          <span key={tag} className="text-[11px] text-foreground-400 bg-background-200/70 px-1.5 py-0.5 rounded whitespace-nowrap">
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </section>

            <section className="py-8 md:py-12">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950">
                  最近更新
                </h2>
                <Link to="/category/all" className="text-sm text-primary-500 hover:text-primary-600 font-medium whitespace-nowrap">
                  全部更新 <i className="ri-arrow-right-line ml-1"></i>
                </Link>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 md:gap-5">
                {latestComics.map((comic) => (
                  <Link
                    key={comic.id}
                    to={`/article/${comic.id}`}
                    className="group bg-background-100 rounded-lg overflow-hidden transition-all hover:bg-background-200/70 cursor-pointer"
                  >
                    <div className="relative w-full aspect-[3/4] overflow-hidden">
                      <img
                        src={comic.cover}
                        alt={`${comic.title} - 韩国漫画`}
                        title={`${comic.title} - 韩国漫画免费在线观看`}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                      <div className="absolute top-2 right-2 bg-accent-500 text-background-50 text-[10px] px-1.5 py-0.5 rounded whitespace-nowrap">
                        {comic.updateDate}
                      </div>
                    </div>
                    <div className="p-3">
                      <h3 className="text-sm font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors truncate">
                        {comic.title}
                      </h3>
                      <p className="text-xs text-foreground-500 mt-1">{comic.author} · {formatViews(comic.views)}阅读</p>
                    </div>
                  </Link>
                ))}
              </div>
            </section>

            <section className="py-8 md:py-12">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-950 mb-6">
                全部韩国漫画
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3 md:gap-4">
                {comics.map((comic) => (
                  <Link
                    key={comic.id}
                    to={`/article/${comic.id}`}
                    className="group bg-background-100 rounded-lg overflow-hidden transition-all hover:bg-background-200/70 cursor-pointer"
                  >
                    <div className="relative w-full aspect-[3/4] overflow-hidden">
                      <img
                        src={comic.cover}
                        alt={`${comic.title} - 韩国漫画`}
                        title={`${comic.title} - 韩国漫画免费在线观看`}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                      />
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-2">
                        <span className="text-white text-[10px] opacity-90">{comic.status}</span>
                      </div>
                    </div>
                    <div className="p-2.5">
                      <h3 className="text-xs font-semibold text-foreground-900 group-hover:text-primary-600 transition-colors truncate">
                        {comic.title}
                      </h3>
                      <p className="text-[10px] text-foreground-500 mt-0.5 truncate">{comic.author}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </section>

            <section className="py-8 md:py-12">
              <div className="bg-background-100 rounded-lg p-6 md:p-8 text-center">
                <h2 className="text-lg md:text-xl font-heading font-bold text-foreground-950 mb-2">
                  找不到想看的韩国漫画？
                </h2>
                <p className="text-sm text-foreground-600 mb-4 max-w-md mx-auto">
                  告诉我们你想看的韩漫，我们会尽快加入资源库。韩国漫画网持续更新，为您提供最全的韩漫大全。
                </p>
                <div className="flex items-center max-w-md mx-auto gap-2">
                  <input
                    type="text"
                    placeholder="输入漫画名称..."
                    className="flex-1 px-4 py-2.5 text-sm bg-background-50 border border-background-200/70 rounded-lg text-foreground-900 placeholder-foreground-400 focus:outline-none focus:border-primary-300 transition-all"
                  />
                  <button className="px-5 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium rounded-lg transition-all whitespace-nowrap">
                    提交
                  </button>
                </div>
              </div>
            </section>
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
}